import { useEffect, useRef, useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import MissionShell, { ErrorNotice } from '../MissionShell/MissionShell';
import { downloadWithAuth } from '../../api/authInterceptor';
import { asCsvUrl, AsQuery, AsReport, AsStatusCount, fetchAsReport } from '../../api/batch14Api';

const STEPS = [
  'The report loads live from the workflow API (cached for 5 minutes). Use Refresh data to reload it.',
  'Narrow it with the date range and the Client, Sub-client, Phase, Team Leader and Status filters. Each filter only offers values that exist under the others.',
  'Each cell shows the status of that client / sub-client in that phase. Download the filtered rows as CSV if needed.',
];

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

function MultiSelect({
  label,
  placeholder,
  options,
  selected,
  onChange,
}: {
  label: string;
  placeholder: string;
  options: string[];
  selected: string[];
  onChange: (values: string[]) => void;
}) {
  return (
    <details className="relative rounded-md border border-input bg-background">
      <summary className="flex cursor-pointer list-none items-center justify-between px-3 py-2 text-sm">
        <span className="font-medium text-foreground">{label}</span>
        <span className="ml-2 truncate text-xs text-muted-foreground">
          {selected.length === 0 ? placeholder : `${selected.length} selected`}
        </span>
      </summary>
      <div className="max-h-56 overflow-auto border-t border-border px-3 py-2">
        {options.length === 0 && <p className="text-xs text-muted-foreground">No options</p>}
        {options.map((o) => (
          <label key={o} className="flex items-center gap-2 py-0.5 text-sm text-foreground">
            <input
              type="checkbox"
              checked={selected.includes(o)}
              onChange={(e) => onChange(e.target.checked ? [...selected, o] : selected.filter((v) => v !== o))}
            />
            {o}
          </label>
        ))}
      </div>
    </details>
  );
}

function AsWorkflowPanel({ mission, onBack }: PanelProps) {
  const [report, setReport] = useState<AsReport | null>(null);
  const [selections, setSelections] = useState<Record<string, string[]>>({});
  const [dateStart, setDateStart] = useState<string | null>(null);
  const [dateEnd, setDateEnd] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const refreshNext = useRef(false);

  // Load whenever the filters change. After a response, drop any selection that is no longer an option.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const query: AsQuery = { dateStart, dateEnd, selections, refresh: refreshNext.current };
    refreshNext.current = false;
    fetchAsReport(query)
      .then((data) => {
        if (cancelled) return;
        setReport(data);
        setError(null);
        if (dateStart === null) setDateStart(data.date_range[0]);
        if (dateEnd === null) setDateEnd(data.date_range[1]);
        const pruned = Object.fromEntries(
          Object.entries(selections).map(([key, values]) => [key, values.filter((v) => data.options[key]?.includes(v))])
        );
        if (JSON.stringify(pruned) !== JSON.stringify(selections)) setSelections(pruned);
      })
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : 'Could not load the workflow data.'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selections, dateStart, dateEnd, refreshTick]);

  const refresh = () => {
    refreshNext.current = true;
    setRefreshTick((t) => t + 1);
  };

  const renderCell = (counts: AsStatusCount[] | undefined) => {
    if (!counts || counts.length === 0) return null;
    const meta = report?.status_meta ?? {};
    const lead = meta[counts[0].status];
    return (
      <span style={lead ? { backgroundColor: `${lead.color}22` } : undefined} className="block rounded px-2 py-1">
        {counts.length === 1
          ? meta[counts[0].status]?.label ?? counts[0].status
          : counts.map((c) => `${meta[c.status]?.label ?? c.status} (${c.count})`).join(' | ')}
      </span>
    );
  };

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS} wide>
      {error && (
        <div className="mb-4">
          <ErrorNotice message={error} />
          <Button className="mt-3" variant="outline" onClick={refresh}>
            🔄 Retry
          </Button>
        </div>
      )}

      {report && (
        <>
          <div className="mb-4 rounded-lg border border-border bg-muted p-4">
            <p className="mb-3 text-sm font-bold text-foreground">🔍 Filters</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="as-from" className="mb-1.5">
                  Job received from
                </Label>
                <Input
                  id="as-from"
                  type="date"
                  min={report.date_min}
                  max={report.date_max}
                  value={dateStart ?? report.date_range[0]}
                  onChange={(e) => setDateStart(e.target.value || null)}
                />
              </div>
              <div>
                <Label htmlFor="as-to" className="mb-1.5">
                  Job received to
                </Label>
                <Input
                  id="as-to"
                  type="date"
                  min={report.date_min}
                  max={report.date_max}
                  value={dateEnd ?? report.date_range[1]}
                  onChange={(e) => setDateEnd(e.target.value || null)}
                />
              </div>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {report.filters.map((f) => (
                <MultiSelect
                  key={f.key}
                  label={f.label}
                  placeholder={f.placeholder}
                  options={report.options[f.key] ?? []}
                  selected={selections[f.key] ?? []}
                  onChange={(values) => setSelections((cur) => ({ ...cur, [f.key]: values }))}
                />
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Button variant="outline" onClick={refresh} disabled={loading}>
                🔄 Refresh data
              </Button>
              <button
                type="button"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm font-medium text-foreground hover:bg-muted"
                onClick={() =>
                  downloadWithAuth(asCsvUrl({ dateStart, dateEnd, selections }), 'as_workflow_filtered.csv').catch(() =>
                    window.alert('Could not download the CSV. Try signing in again.'),
                  )
                }
              >
                ⬇ Download filtered CSV
              </button>
              <span className="text-xs text-muted-foreground">
                {loading ? 'Loading…' : `Filtered rows: ${report.filtered_records.toLocaleString()} / ${report.total_records.toLocaleString()}`}
              </span>
            </div>
          </div>

          <h3 className="mb-2 text-sm font-bold text-foreground">Client › Sub-client: phase-wise status</h3>
          {report.rows.length === 0 ? (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">No data matches the current filters.</p>
          ) : (
            <div className="max-h-[560px] overflow-auto rounded-md border border-border">
              <table className="w-full text-left text-xs">
                <thead className="sticky top-0 z-10 bg-[var(--panel-2)] text-muted-foreground shadow-[0_1px_0_var(--line)]">
                  <tr>
                    <th className="bg-[var(--panel-2)] px-3 py-2 font-semibold">#</th>
                    <th className="bg-[var(--panel-2)] px-3 py-2 font-semibold">Client Name</th>
                    <th className="bg-[var(--panel-2)] px-3 py-2 font-semibold">Sub client name</th>
                    {report.phases.map((p) => (
                      <th key={p} className="min-w-[140px] bg-[var(--panel-2)] px-3 py-2 font-semibold">
                        {p}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.rows.map((row, i) => (
                    <tr key={`${row.client}|${row.sub_client}`} className="border-t border-border align-top">
                      <td className="px-3 py-1.5 text-muted-foreground">{i + 1}</td>
                      <td className="px-3 py-1.5 font-medium text-foreground">{row.client}</td>
                      <td className="px-3 py-1.5 text-foreground">{row.sub_client}</td>
                      {report.phases.map((p) => (
                        <td key={p} className="px-2 py-1 text-foreground">
                          {renderCell(row.phases[p])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <details className="mt-4 rounded-lg border border-border bg-muted px-4 py-3">
            <summary className="cursor-pointer text-sm font-semibold text-foreground">
              🗂 View raw filtered data (first {report.raw_rows.length} rows)
            </summary>
            <div className="mt-2 max-h-80 overflow-auto rounded-md border border-border bg-background">
              <table className="w-full text-left text-xs">
                <thead className="sticky top-0 z-10 bg-[var(--panel-2)] text-muted-foreground shadow-[0_1px_0_var(--line)]">
                  <tr>
                    {report.raw_columns.map((c) => (
                      <th key={c} className="bg-[var(--panel-2)] px-3 py-1.5 font-semibold">
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.raw_rows.map((row, i) => (
                    <tr key={i} className="border-t border-border">
                      {row.map((cell, j) => (
                        <td key={j} className="px-3 py-1.5">
                          {cell === null ? '' : String(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </>
      )}
      {loading && !report && !error && <p className="text-sm text-muted-foreground">Loading A&amp;S workflow data…</p>}
    </MissionShell>
  );
}

export default AsWorkflowPanel;
